import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, Icons, Tooltip, Typography } from '../../../../../../stories/atoms';
import {
  ColumnFlexContainer,
  ContainerV2,
  RowFlexContainer,
} from '../../../../../../stories/atoms/containerV2/ContainerV2';
import { Charts } from '../../../../../../stories/molecules/charts/Charts';
import { homeTranslations } from '../../../../../../translations';
import { formatValue } from '../../../../../../utils';
import { CustomerEngagementTranslation, EngagementMetrics } from '../CustomerEngagement';
import { StyledMetricTile, StyledTypography } from '../CustomerEngagement.styles';

const { customerEngagement: customerEngagementranslations } = homeTranslations;

type EngagementMetricTooltipProps = {
  metricName: EngagementMetrics;
  tooltipText: string;
  icon: Icons;
  value: string;
};

const EngagementMetricTooltip: React.FC<EngagementMetricTooltipProps> = ({
  metricName,
  tooltipText,
  icon,
  value,
}) => {
  const { t } = useTranslation();
  return (
    <Tooltip
      arrowedToolTip
      text={
        <ColumnFlexContainer padding={[1, 2]}>
          <Typography color="white">
            {t(
              customerEngagementranslations[tooltipText as CustomerEngagementTranslation].key,
              customerEngagementranslations[tooltipText as CustomerEngagementTranslation].defaultValue,
            )}
            : {value}%
          </Typography>
        </ColumnFlexContainer>
      }
    >
      <StyledMetricTile metricName={metricName} borderRadius={[4]} padding={[2]} gap={[1]} alignItems="center">
        <ContainerV2 padding={[0]} borderRadius={[4]}>
          <Icon icon={icon} size="small" />
        </ContainerV2>
        <Typography>{value}%</Typography>
      </StyledMetricTile>
    </Tooltip>
  );
};

export type EngagementOverviewProps = {
  totalCustomerEngaged: number;
  storeTraffic: number;
  memberRecruited: number;
  memberIdentification: number;
  articleLookups: number;
};

export const EngagementOverview: FC<EngagementOverviewProps> = ({
  totalCustomerEngaged,
  storeTraffic,
  memberRecruited,
  memberIdentification,
  articleLookups,
}) => {
  const { t } = useTranslation();
  const customerEngagementPercentage = storeTraffic === 0 ? 0 : (totalCustomerEngaged / storeTraffic) * 100;

  const memberRecruitedPercentage =
    totalCustomerEngaged === 0 ? 0 : (memberRecruited / totalCustomerEngaged) * 100;
  const memberIdentificationPercentage =
    totalCustomerEngaged === 0 ? 0 : (memberIdentification / totalCustomerEngaged) * 100;
  const articleLookupsPercentage = totalCustomerEngaged === 0 ? 0 : (articleLookups / totalCustomerEngaged) * 100;

  return (
    <ColumnFlexContainer data-testid="engagment-overview-wrapper" width="35%">
      <RowFlexContainer>
        <StyledTypography>
          {t(customerEngagementranslations.title.key, customerEngagementranslations.title.defaultValue)}%
        </StyledTypography>
        <RowFlexContainer>
          <StyledTypography textAlign="end" type="h4">
            {customerEngagementPercentage.toFixed(2)}%
          </StyledTypography>
          <Tooltip
            text={
              <Typography type="caption" color="white">
                {t(
                  customerEngagementranslations.customerEngagementPercentageTooltip.key,
                  customerEngagementranslations.customerEngagementPercentageTooltip.defaultValue,
                )}
              </Typography>
            }
            position="top"
            arrowedToolTip
          >
            <Icon icon={Icons.INFO} size="small" />
          </Tooltip>
        </RowFlexContainer>
      </RowFlexContainer>
      <ColumnFlexContainer height="100%" position="relative" flex={1} data-testid="customer-engagement-chart">
        <Charts
          chartType="donut"
          chartSeries={[memberRecruitedPercentage, memberIdentificationPercentage, articleLookupsPercentage]}
          xAxisCategories={['', '', '']}
          areaChartGradient={{
            type: ['solid'],
          }}
          chartColors={['info', 'warning', 'successLight']}
          chartPlotOptions={{
            customScale: 1.1,
            startAngle: 0,
            endAngle: 360,
            offsetY: 15,
            donut: {
              size: '90%',
              labels: {
                show: false,
              },
            },
          }}
          legend={{
            show: false,
          }}
          strokeWidth={[0]}
        />
        <ColumnFlexContainer
          width="100%"
          height="100%"
          position="absolute"
          top="0px"
          left="0px"
          justifyContent="center"
          alignItems="center"
        >
          <Typography type="legal">
            {t(
              customerEngagementranslations.customerEngaged.key,
              customerEngagementranslations.customerEngaged.defaultValue,
            )}
          </Typography>
          <RowFlexContainer gap={[1]} alignItems="center">
            <Typography type="button">
              {formatValue(totalCustomerEngaged, {
                options: { minimumFractionDigits: 0, notation: 'compact' },
              })}
            </Typography>
            <Tooltip
              text={
                <Typography type="caption" color="white">
                  {t(
                    customerEngagementranslations.totalCustomerEngagedTooltip.key,
                    customerEngagementranslations.totalCustomerEngagedTooltip.defaultValue,
                  )}
                </Typography>
              }
              position="top"
              arrowedToolTip
            >
              <Icon icon={Icons.INFO} size="small" />
            </Tooltip>
          </RowFlexContainer>
        </ColumnFlexContainer>
      </ColumnFlexContainer>

      <RowFlexContainer justifyContent="between">
        <EngagementMetricTooltip
          key="memberRecruitedTooltip1"
          metricName="memberRecruited"
          tooltipText="memberRecruited"
          icon={Icons.NEWMEMBER}
          value={memberRecruitedPercentage.toFixed()}
        />
        <EngagementMetricTooltip
          key="memberIdentificationTooltip1"
          metricName="memberIdentification"
          tooltipText="membersIdentified"
          icon={Icons.BAG}
          value={memberIdentificationPercentage.toFixed()}
        />
        <EngagementMetricTooltip
          key="articleLookupsTooltip1"
          metricName="articleLookups"
          tooltipText="articleLookups"
          icon={Icons.SEARCH}
          value={articleLookupsPercentage.toFixed()}
        />
      </RowFlexContainer>
      <RowFlexContainer
        justifyContent="between"
        alignItems="center"
        backgroundColor="backgroundSelectedDark"
        padding={[1, 2]}
        borderRadius={[3]}
        margin={[2, 0, 0]}
      >
        <RowFlexContainer alignItems="center">
          <Typography type="legal" color="textGray">
            {t(
              customerEngagementranslations.storeTraffic.key,
              customerEngagementranslations.storeTraffic.defaultValue,
            )}
            :{' '}
          </Typography>
          <Typography>
            {formatValue(storeTraffic, {
              options: { minimumFractionDigits: 0, notation: 'compact' },
            })}
          </Typography>
        </RowFlexContainer>
        <Tooltip
          arrowedToolTip
          text={
            <ColumnFlexContainer flexWrap="wrap" padding={[1, 2]}>
              <Typography color="white">
                {t(
                  customerEngagementranslations.storeTrafficTooltip.key,
                  customerEngagementranslations.storeTrafficTooltip.defaultValue,
                )}
              </Typography>
            </ColumnFlexContainer>
          }
        >
          <Icon icon={Icons.INFO} size="small" />
        </Tooltip>
      </RowFlexContainer>
    </ColumnFlexContainer>
  );
};
